import type { CollectionCodeDataItem, ResolvedMonospaceCollection } from '#layers/data-model/public';
import type { CollectionCodeKey, CollectionCodeSnippetOptions } from './types';
import { getCollectionPrimaryKeyFields } from '#layers/data-model/public';

/**
 * Converts a key value into an equality filter keyed by the collection primary key fields.
 */
export function getCollectionPrimaryKeyFilter(collection: ResolvedMonospaceCollection, key: CollectionCodeKey): CollectionCodeDataItem {
	const keyEntries = getCollectionPrimaryKeyFilterEntries(collection, key);

	return Object.fromEntries(
		keyEntries.map(([fieldName, value]) => [fieldName, { _eq: value }]),
	) as CollectionCodeDataItem;
}

/**
 * Returns the REST path segment used by readOne, updateOne, and deleteOne snippets.
 */
export function getRestReadOneKey(options: CollectionCodeSnippetOptions): string {
	if (options.key !== undefined) {
		return formatRestPathKey(options.key);
	}

	const primaryKeyFields = getCollectionPrimaryKeyFields(options.collection);

	if (primaryKeyFields.length === 1) {
		return `{${primaryKeyFields[0]!.apiName}}`;
	}

	return '{key}';
}

/**
 * Converts scalar or object key input into primary-key field/value pairs.
 */
function getCollectionPrimaryKeyFilterEntries(collection: ResolvedMonospaceCollection, key: CollectionCodeKey): [string, string | number | boolean | null][] {
	if (typeof key !== 'object') {
		const [field] = getCollectionPrimaryKeyFields(collection);
		return field ? [[field.apiName, key]] : [];
	}

	const primaryKeyFields = getCollectionPrimaryKeyFields(collection);

	if (primaryKeyFields.length === 0) {
		return Object.entries(key);
	}

	return primaryKeyFields
		.map((field) => [field.apiName, key[field.apiName]] as [string, string | number | boolean | null | undefined])
		.filter((entry): entry is [string, string | number | boolean | null] => entry[1] !== undefined);
}

/**
 * Encodes a primary key for use in a REST item path.
 */
function formatRestPathKey(key: CollectionCodeKey): string {
	if (typeof key === 'object') {
		return encodeURIComponent(JSON.stringify(key));
	}

	return encodeURIComponent(String(key));
}
